import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  historyActionLabel,
  historyFieldLabel,
  historyValueLabel,
} from '@/lib/job-history-format';

/**
 * Historial de la ficha del trabajo (Fase 1 trabajo manual): quién editó qué
 * y cuándo, con antes → después. Solo correcciones de ficha — los cambios de
 * estado/ponche son el ciclo de vida y se ven en la tarjeta del trabajo.
 */
export function JobHistoryModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['jobs', job?.id, 'history'],
    queryFn: () => jobsApi.getJobHistory(job!.id),
    enabled: Boolean(job),
  });

  const items = data ?? [];

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="Historial de la ficha"
      description="Correcciones hechas a este trabajo: quién, cuándo y qué cambió."
      footer={
        <Button variant="ghost" type="button" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={History}
          title="Sin ediciones registradas"
          description="Cuando alguien corrija la ficha (datos, cita, técnico, despacho), quedará registrado aquí."
        />
      ) : (
        <ul className="max-h-96 space-y-3 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item.id}
              className="rounded-md border border-surface-border p-3 text-sm dark:border-slate-700"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {item.userName}
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    {historyActionLabel(item.action)}
                  </span>
                </p>
                <p className="text-xs text-slate-400">{historyValueLabel(item.at)}</p>
              </div>
              {item.changes && (
                <ul className="mt-1.5 space-y-0.5">
                  {Object.entries(item.changes).map(([campo, cambio]) => (
                    <li key={campo} className="text-xs text-slate-600 dark:text-slate-300">
                      <span className="font-medium">{historyFieldLabel(campo)}:</span>{' '}
                      <span className="text-slate-400 line-through">
                        {historyValueLabel(cambio.antes)}
                      </span>{' '}
                      → {historyValueLabel(cambio.despues)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
